// ---------------------------------------------------------------------------
// Filter Options Service
// ---------------------------------------------------------------------------
// Fetches dimension options from backend for filter dropdown population.
// Maps frontend MoreFiltersState to backend API parameters with:
//   - Nested state flattening
//   - String ID → integer DB ID resolution
//   - Datasource-specific param key mapping
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'
import type { MoreFiltersState, FilterCategoryKey } from '@/components/v11/discovery/filters/filterTypes'
import type { DataSourceId, FilterChip } from '@/components/v11/discovery/types'
import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  AGE_LABELS,
  GENDER_OPTIONS,
  ETHNICITY_OPTIONS,
  INDUSTRY_OPTIONS,
  LIFESTYLE_OPTIONS,
  STATUS_CLASS_OPTIONS,
  EMOTION_OPTIONS,
  MEDIA_TYPE_OPTIONS,
  CONTENT_CATEGORY_OPTIONS,
  BRAND_CATEGORY_OPTIONS,
  EMPTY_FILTER_STATE,
} from '@/components/v11/discovery/filters/filterConstants'
import { FOLLOWER_TIERS } from '@/components/v11/discovery/constants'
import { GENERATION_OPTIONS } from '@/components/v11/funnel/constants'

export interface DimensionOption {
  readonly id: number
  readonly name: string
}

export interface EmotionOption extends DimensionOption {
  readonly category: string
  readonly color: string
  readonly icon: string
}

export interface BrandSubcategoryOption {
  readonly id: number
  readonly name: string
  readonly categoryId: number
}

export interface FilterOptions {
  readonly platforms: ReadonlyArray<DimensionOption>
  readonly genders: ReadonlyArray<DimensionOption>
  readonly ageIntervals: ReadonlyArray<DimensionOption>
  readonly countries: ReadonlyArray<DimensionOption>
  readonly ethnicities: ReadonlyArray<DimensionOption>
  readonly lifestyles: ReadonlyArray<DimensionOption>
  readonly industries: ReadonlyArray<DimensionOption>
  readonly followerTiers: ReadonlyArray<DimensionOption>
  readonly continents: ReadonlyArray<DimensionOption>
  readonly statusClasses: ReadonlyArray<DimensionOption>
  readonly emotions: ReadonlyArray<EmotionOption>
  readonly languages: ReadonlyArray<DimensionOption>
  readonly brandCategories: ReadonlyArray<DimensionOption>
  readonly brandSubcategories: ReadonlyArray<BrandSubcategoryOption>
}

export interface FilterOptionsResponse {
  readonly success: boolean
  readonly data: FilterOptions
}

/**
 * Fetch all filter dimension options from backend
 * Response is cached by TanStack Query (5 minutes stale time)
 */
export async function getFilterOptions(): Promise<FilterOptions> {
  try {
    // httpClient.get() already returns response.data, so `result` is the JSON body
    // { success: true, data: { platforms: [...], genders: [...], ... } }
    const result = await api.get<FilterOptionsResponse>('/api/v11/discovery/filters')
    // Access the nested `data` field from the JSON body
    return (result as unknown as FilterOptionsResponse)?.data ?? {} as FilterOptions
  } catch (error) {
    console.error('Failed to fetch filter options:', error)
    return {
      platforms: [],
      genders: [],
      ageIntervals: [],
      countries: [],
      ethnicities: [],
      lifestyles: [],
      industries: [],
      followerTiers: [],
      continents: [],
      statusClasses: [],
      emotions: [],
      languages: [],
      brandCategories: [],
      brandSubcategories: [],
    }
  }
}

// ---------------------------------------------------------------------------
// ID Resolution Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve frontend string IDs to backend integer DB IDs.
 * Matches frontend values against DB option names (case-insensitive).
 *
 * @param frontendValues - String IDs from filter UI (e.g., ['male', 'female'])
 * @param dbOptions - DB options with {id, name} (e.g., [{id:1, name:'Male'}])
 * @param labelLookup - Optional map from frontend ID → display label for
 *   cases where the frontend ID differs from the DB name (e.g., 'us' → 'United States')
 */
export function resolveIds(
  frontendValues: ReadonlyArray<string>,
  dbOptions: ReadonlyArray<DimensionOption>,
  labelLookup?: Readonly<Record<string, string>>
): ReadonlyArray<number> {
  if (frontendValues.length === 0 || dbOptions.length === 0) return []

  return frontendValues.flatMap((val) => {
    const lookupName = labelLookup?.[val] ?? val
    const match = dbOptions.find(
      (opt) => opt.name.toLowerCase() === lookupName.toLowerCase()
    )
    return match ? [match.id] : []
  })
}

/**
 * Resolve age/generation values to backend age interval IDs.
 * Supports both:
 * - Generation keys from GENERATION_OPTIONS (e.g., 'gen_z', 'millennials')
 * - Marketing labels from AGE_LABELS (e.g., 'Millennials', 'Gen Z')
 */
function resolveAgeIds(frontendValues: ReadonlyArray<string>): ReadonlyArray<number> {
  if (frontendValues.length === 0) return []

  return frontendValues.flatMap((val) => {
    // Check GENERATION_OPTIONS first (for generation keys like 'gen_z')
    const genMatch = GENERATION_OPTIONS.find((g) => g.key === val)
    if (genMatch) return genMatch.ids

    // Fallback to AGE_LABELS (for marketing names like 'Millennials')
    const match = AGE_LABELS.find(
      (label) => label.marketing.toLowerCase() === val.toLowerCase()
    )
    return match ? match.ageIntervalIds : []
  })
}

/**
 * Build a lookup map from frontend country code → country label.
 * e.g., { us: 'United States', br: 'Brazil' }
 */
const COUNTRY_LABEL_LOOKUP: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(COUNTRY_OPTIONS.map((c) => [c.id, c.label]))
)

/**
 * Build a lookup map from frontend language code → language label.
 * e.g., { en: 'English', es: 'Spanish' }
 */
const LANGUAGE_LABEL_LOOKUP: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(LANGUAGE_OPTIONS.map((l) => [l.id, l.label]))
)

/**
 * Build a lookup map from frontend lifestyle ID → DB lifestyle name.
 * Frontend IDs (luxury, adventure, etc.) don't match DB names exactly.
 *
 * Frontend: { id: 'adventure', label: 'Adventure' }
 * DB:       { id: 5, name: 'Adventurous/Explorer' }
 */
const LIFESTYLE_LABEL_LOOKUP: Readonly<Record<string, string>> = Object.freeze({
  'luxury': 'Luxurious/High-end',
  'wellness': 'Active/Sporty',
  'adventure': 'Adventurous/Explorer',
  'urban': 'Cultural/Creative',
  'family': 'Family-oriented',
})

/**
 * Build a lookup map from frontend brand category ID → DB category name.
 * Frontend IDs (apparel, beverages, etc.) don't match DB names directly,
 * so we map frontend labels to let resolveIds match against DB names.
 *
 * Frontend: { id: 'apparel', label: 'Apparel & Footwear' }
 * DB:       { id: 1, name: 'Fashion & Apparel' }
 *
 * When the DB name doesn't match the frontend label, we map explicitly.
 */
const BRAND_CATEGORY_LABEL_LOOKUP: Readonly<Record<string, string>> = Object.freeze({
  'apparel': 'Fashion & Apparel',        // DB id: 1
  'sports-equipment': 'Sports',           // DB id: 2
  'automotive': 'Automotive & Transportation', // DB id: 3
  'technology': 'Electronics',            // DB id: 4
  'beverages': 'Food & Beverages',        // DB id: 6
  'financial': 'Financial Services',      // DB id: 8
  'telecom': 'Telecommunications',        // DB id: 29
  'food': 'Food & Beverages',             // DB id: 6 (same as beverages)
  'health': 'Health & Wellness',          // DB id: 31
  'media': 'Media & Entertainment',       // DB id: 28
  'travel': 'Travel & Hospitality',       // DB id: 30
  'retail': 'Retail & E-Commerce',        // DB id: 27
})

/**
 * Reverse lookup: subcategory ID → parent category ID.
 * Built from BRAND_CATEGORY_OPTIONS so HierarchicalSelect subcategory
 * selections can be mapped back to parent categories for DB resolution.
 */
const SUBCATEGORY_TO_PARENT: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    BRAND_CATEGORY_OPTIONS.flatMap((cat) =>
      cat.subcategories.map((sub) => [sub.id, cat.id])
    )
  )
)

// ---------------------------------------------------------------------------
// Datasource-Specific Param Key Maps
// ---------------------------------------------------------------------------

/** Maps frontend filter keys to backend API param names per datasource. */
const PARAM_KEYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  profiles: {
    gender: 'gender',
    age: 'age',
    country: 'country',
    ethnicity: 'ethnicity',
    lifestyle: 'lifestyle',
    industry: 'industry',
    followerTier: 'follower_tier',
    continent: 'continent',
    statusClass: 'status_class',
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
  },
  content: {
    gender: 'author_gender',
    country: 'author_country',
    categories: 'category',
    mediaType: 'media_type',
  },
  comments: {
    gender: 'author_gender',
    age: 'author_age',
    country: 'author_country',
    emotions: 'emotion',
    language: 'language',
    toxic: 'toxic',
    spam: 'spam',
  },
  brands: {
    // Brand-specific filters
    brandCategory: 'category',
    // Author filters (same as profiles)
    gender: 'gender',
    age: 'age',
    ethnicity: 'ethnicity',
    country: 'country',
    followerTier: 'follower_tier',
    industry: 'industry',
    lifestyle: 'lifestyle',
    statusClass: 'status_class',
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
  },
  teams: {
    // Team-specific filters
    country: 'country',  // Note: This is team's country, not author's
    // Author filters (same as profiles)
    gender: 'gender',
    age: 'age',
    ethnicity: 'ethnicity',
    followerTier: 'follower_tier',
    industry: 'industry',
    lifestyle: 'lifestyle',
    statusClass: 'status_class',
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
  },
} as const

// ---------------------------------------------------------------------------
// Main Mapping Function
// ---------------------------------------------------------------------------

/**
 * Map frontend MoreFiltersState to backend API parameters.
 *
 * Handles:
 * 1. Nested state flattening (demographics.gender → gender)
 * 2. String ID → integer DB ID resolution via FilterOptions
 * 3. Datasource-specific param key mapping (gender → author_gender for Content)
 * 4. Sentiment scale conversion (0-100 → 0-10 for Comments)
 * 5. Toggle/boolean filters (yes/no/all → 'true'/'false')
 * 6. Skipping filters not applicable to current datasource
 */
export function mapFiltersToApiParams(
  moreFilters: MoreFiltersState,
  dataSource: DataSourceId,
  filterOptions?: FilterOptions
): Record<string, unknown> {
  const params: Record<string, unknown> = {}
  const keys = PARAM_KEYS[dataSource]
  if (!keys) return params

  // -- Array filters (demographics) --

  if (keys.gender && moreFilters.demographics.gender.length > 0) {
    const ids = filterOptions?.genders
      ? resolveIds(moreFilters.demographics.gender, filterOptions.genders)
      : []
    if (ids.length > 0) params[keys.gender] = ids
  }

  if (keys.age && moreFilters.demographics.age.length > 0) {
    const ids = resolveAgeIds(moreFilters.demographics.age)
    if (ids.length > 0) params[keys.age] = ids
  }

  if (keys.country && moreFilters.demographics.country.length > 0) {
    const ids = filterOptions?.countries
      ? resolveIds(moreFilters.demographics.country, filterOptions.countries, COUNTRY_LABEL_LOOKUP)
      : []
    if (ids.length > 0) params[keys.country] = ids
  }

  if (keys.ethnicity && moreFilters.demographics.ethnicity.length > 0) {
    const ids = filterOptions?.ethnicities
      ? resolveIds(moreFilters.demographics.ethnicity, filterOptions.ethnicities)
      : []
    if (ids.length > 0) params[keys.ethnicity] = ids
  }

  if (keys.industry && moreFilters.demographics.industry.length > 0) {
    const ids = filterOptions?.industries
      ? resolveIds(moreFilters.demographics.industry, filterOptions.industries)
      : []
    if (ids.length > 0) params[keys.industry] = ids
  }

  if (keys.followerTier && moreFilters.demographics.followerTier.length > 0) {
    const ids = filterOptions?.followerTiers
      ? resolveIds(moreFilters.demographics.followerTier, filterOptions.followerTiers)
      : []
    if (ids.length > 0) params[keys.followerTier] = ids
  }

  // -- Psychographics --

  if (keys.lifestyle && moreFilters.psychographics.lifestyle.length > 0) {
    const ids = filterOptions?.lifestyles
      ? resolveIds(moreFilters.psychographics.lifestyle, filterOptions.lifestyles, LIFESTYLE_LABEL_LOOKUP)
      : []
    if (ids.length > 0) params[keys.lifestyle] = ids
  }

  if (keys.statusClass && moreFilters.psychographics.statusClass.length > 0) {
    const ids = filterOptions?.statusClasses
      ? resolveIds(moreFilters.psychographics.statusClass, filterOptions.statusClasses)
      : []
    if (ids.length > 0) params[keys.statusClass] = ids
  }

  // -- Behaviour --

  if (keys.emotions && moreFilters.behaviour.emotions.length > 0) {
    const ids = filterOptions?.emotions
      ? resolveIds(moreFilters.behaviour.emotions, filterOptions.emotions)
      : []
    if (ids.length > 0) params[keys.emotions] = ids
  }

  if (keys.language && moreFilters.behaviour.language.length > 0) {
    const ids = filterOptions?.languages
      ? resolveIds(moreFilters.behaviour.language, filterOptions.languages, LANGUAGE_LABEL_LOOKUP)
      : []
    if (ids.length > 0) params[keys.language] = ids
  }

  // Sentiment range (Comments only, scale 0-100 → 0-10)
  if (dataSource === 'comments') {
    const { min, max } = moreFilters.behaviour.sentiment
    const isNonDefault = min !== 0 || max !== 100
    if (isNonDefault) {
      params.sentiment_min = Math.round(min / 10)
      params.sentiment_max = Math.round(max / 10)
    }
  }

  // -- Content filters --

  if (keys.categories && moreFilters.content.categories.length > 0) {
    // Content categories - resolve via label matching if filterOptions available
    // For now, pass string values (backend may need integer IDs)
    // TODO: Add categories dimension to FilterOptions endpoint
    params[keys.categories] = moreFilters.content.categories
  }

  if (keys.mediaType && moreFilters.content.mediaType.length > 0) {
    // Media types - resolve via label matching if filterOptions available
    // TODO: Add media_types dimension to FilterOptions endpoint
    params[keys.mediaType] = moreFilters.content.mediaType
  }

  // -- Entity filters (Brands/Teams datasources) --

  if (keys.brandCategory && moreFilters.entities.brandCategory.length > 0) {
    // HierarchicalSelect stores subcategory IDs (e.g. 'equipment-soccer').
    // Reverse-map to unique parent category IDs, then resolve to DB integers.
    const parentCategoryIds = [
      ...new Set(
        moreFilters.entities.brandCategory
          .map((subId) => SUBCATEGORY_TO_PARENT[subId] ?? subId)
      ),
    ]
    const ids = filterOptions?.brandCategories
      ? resolveIds(parentCategoryIds, filterOptions.brandCategories, BRAND_CATEGORY_LABEL_LOOKUP)
      : []
    if (ids.length > 0) params[keys.brandCategory] = ids
  }

  // -- Quality / Toggle filters --

  if (keys.verified && moreFilters.quality.verified !== 'all') {
    params[keys.verified] = moreFilters.quality.verified === 'yes' ? 'true' : 'false'
  }

  if (keys.famous && moreFilters.quality.famous !== 'all') {
    params[keys.famous] = moreFilters.quality.famous === 'yes' ? 'true' : 'false'
  }

  if (keys.isPerson && moreFilters.quality.isPerson !== 'all') {
    params[keys.isPerson] = moreFilters.quality.isPerson === 'yes' ? 'true' : 'false'
  }

  if (keys.toxic && moreFilters.quality.toxic !== 'all') {
    params[keys.toxic] = moreFilters.quality.toxic === 'yes' ? 'true' : 'false'
  }

  if (keys.spam && moreFilters.quality.spam !== 'all') {
    params[keys.spam] = moreFilters.quality.spam === 'yes' ? 'true' : 'false'
  }

  return params
}

// ---------------------------------------------------------------------------
// Filter Chips (toolbar display)
// ---------------------------------------------------------------------------

/** Lookup maps from filter option ID → display label */
const LABEL_LOOKUPS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  gender: Object.freeze(Object.fromEntries(GENDER_OPTIONS.map((o) => [o.id, o.label]))),
  age: Object.freeze(Object.fromEntries(AGE_LABELS.map((o) => [o.marketing, `${o.marketing} (${o.range})`]))),
  ethnicity: Object.freeze(Object.fromEntries(ETHNICITY_OPTIONS.map((o) => [o.id, o.label]))),
  country: Object.freeze(Object.fromEntries(COUNTRY_OPTIONS.map((o) => [o.id, o.label]))),
  followerTier: Object.freeze(Object.fromEntries(FOLLOWER_TIERS.map((o) => [o.id, `${o.label} (${o.range})`]))),
  industry: Object.freeze(Object.fromEntries(INDUSTRY_OPTIONS.map((o) => [o.id, o.label]))),
  lifestyle: Object.freeze(Object.fromEntries(LIFESTYLE_OPTIONS.map((o) => [o.id, o.label]))),
  statusClass: Object.freeze(Object.fromEntries(STATUS_CLASS_OPTIONS.map((o) => [o.id, o.label]))),
  emotions: Object.freeze(Object.fromEntries(EMOTION_OPTIONS.map((o) => [o.id, o.label]))),
  language: Object.freeze(Object.fromEntries(LANGUAGE_OPTIONS.map((o) => [o.id, o.label]))),
  categories: Object.freeze(Object.fromEntries(CONTENT_CATEGORY_OPTIONS.map((o) => [o.id, o.label]))),
  mediaType: Object.freeze(Object.fromEntries(MEDIA_TYPE_OPTIONS.map((o) => [o.id, o.label]))),
  brandCategory: Object.freeze(Object.fromEntries([
    ...BRAND_CATEGORY_OPTIONS.map((o) => [o.id, o.label]),
    ...BRAND_CATEGORY_OPTIONS.flatMap((cat) =>
      cat.subcategories.map((sub) => [sub.id, sub.label])
    ),
  ])),
}

/** Human-readable labels for filter dimension keys */
const DIMENSION_LABELS: Readonly<Record<string, string>> = {
  gender: 'Gender',
  age: 'Age',
  ethnicity: 'Ethnicity',
  country: 'Country',
  followerTier: 'Follower Tier',
  industry: 'Industry',
  lifestyle: 'Lifestyle',
  statusClass: 'Status Class',
  emotions: 'Emotion',
  language: 'Language',
  categories: 'Category',
  mediaType: 'Media Type',
  brandCategory: 'Brand Category',
  verified: 'Verified',
  famous: 'Famous',
  isPerson: 'Is Person',
  spam: 'Spam',
  toxic: 'Toxic',
  sentiment: 'Sentiment',
}

/**
 * Convert MoreFiltersState to FilterChip[] for toolbar display.
 * Each non-default filter value becomes one chip.
 * Chip IDs encode category.key.value for removal parsing.
 */
export function moreFiltersToChips(filters: MoreFiltersState): ReadonlyArray<FilterChip> {
  const chips: FilterChip[] = []

  const addArrayChips = (
    category: FilterCategoryKey,
    key: string,
    values: ReadonlyArray<string>
  ) => {
    const lookup = LABEL_LOOKUPS[key]
    for (const val of values) {
      chips.push({
        id: `${category}.${key}.${val}`,
        label: DIMENSION_LABELS[key] ?? key,
        value: lookup?.[val] ?? val,
        category,
      })
    }
  }

  const addToggleChip = (
    category: FilterCategoryKey,
    key: string,
    value: string
  ) => {
    if (value !== 'all') {
      const dimensionLabel = DIMENSION_LABELS[key] ?? key
      chips.push({
        id: `${category}.${key}.${value}`,
        label: dimensionLabel,
        value: `${dimensionLabel}: ${value === 'yes' ? 'Yes' : 'No'}`,
        category,
      })
    }
  }

  // Demographics
  addArrayChips('demographics', 'gender', filters.demographics.gender)
  addArrayChips('demographics', 'age', filters.demographics.age)
  addArrayChips('demographics', 'ethnicity', filters.demographics.ethnicity)
  addArrayChips('demographics', 'country', filters.demographics.country)
  addArrayChips('demographics', 'followerTier', filters.demographics.followerTier)
  addArrayChips('demographics', 'industry', filters.demographics.industry)

  // Psychographics
  addArrayChips('psychographics', 'lifestyle', filters.psychographics.lifestyle)
  addArrayChips('psychographics', 'statusClass', filters.psychographics.statusClass)

  // Behaviour
  addArrayChips('behaviour', 'emotions', filters.behaviour.emotions)
  addArrayChips('behaviour', 'language', filters.behaviour.language)

  // Sentiment range
  const { min, max } = filters.behaviour.sentiment
  if (min !== 0 || max !== 100) {
    chips.push({
      id: 'behaviour.sentiment.range',
      label: 'Sentiment',
      value: `${min}–${max}`,
      category: 'behaviour',
    })
  }

  // Content
  addArrayChips('content', 'categories', filters.content.categories)
  addArrayChips('content', 'mediaType', filters.content.mediaType)

  // Entities
  addArrayChips('entities', 'brandCategory', filters.entities.brandCategory)

  // Quality toggles
  addToggleChip('quality', 'isPerson', filters.quality.isPerson)
  addToggleChip('quality', 'verified', filters.quality.verified)
  addToggleChip('quality', 'famous', filters.quality.famous)
  addToggleChip('quality', 'spam', filters.quality.spam)
  addToggleChip('quality', 'toxic', filters.quality.toxic)

  return chips
}

/**
 * Remove a single chip from MoreFiltersState (immutable).
 * Parses the chip ID format: "category.key.value"
 */
export function removeChipFromFilters(
  filters: MoreFiltersState,
  chipId: string
): MoreFiltersState {
  const parts = chipId.split('.')
  if (parts.length < 3) return filters

  const category = parts[0] as FilterCategoryKey
  const key = parts[1]
  const value = parts.slice(2).join('.') // Handle values with dots

  const categoryState = filters[category] as unknown as Record<string, unknown>
  if (!categoryState) return filters

  const currentValue = categoryState[key]

  // Array filter: remove the specific value
  if (Array.isArray(currentValue)) {
    const newArray = currentValue.filter((v: string) => v !== value)
    return {
      ...filters,
      [category]: {
        ...categoryState,
        [key]: newArray,
      },
    }
  }

  // Toggle filter: reset to 'all'
  if (typeof currentValue === 'string') {
    return {
      ...filters,
      [category]: {
        ...categoryState,
        [key]: 'all',
      },
    }
  }

  // Sentiment range: reset to default
  if (key === 'sentiment') {
    return {
      ...filters,
      [category]: {
        ...categoryState,
        sentiment: { min: 0, max: 100 },
      },
    }
  }

  return filters
}
